import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  DEFAULT_TENANT_ID,
  type PromoSurveyInterestKey,
  type PromoSurvey,
  type PromoSurveyResponse,
} from '@kawai/shared';
import { db } from '../firebase.js';

const SURVEYS_COL = 'promo_surveys';
const SURVEY_RESPONSES_COL = 'promo_survey_responses';

/**
 * M-78 (promo): ある業態のアンケート定義を取得する（販促プラン画面のアンケートセクション用 /
 * 新しい順）。
 *
 * Firestore Rules `promo_surveys/{id}` の list は認証済み自テナント。等値条件のみで引くため
 * composite index 不要（`createdAt` 降順は JS ソート / coupons と同方針）。
 */
export async function fetchSurveysByInterestKey(
  tenantId: string,
  interestKey: PromoSurveyInterestKey,
): Promise<PromoSurvey[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(
    collection(db, SURVEYS_COL),
    where('tenantId', '==', tid),
    where('interestKey', '==', interestKey),
  );
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<PromoSurvey, 'id'>),
  }));
  return items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/**
 * M-78 (promo): 自テナントの全アンケート定義を取得する（配信作成画面のリンク挿入 select 用 /
 * 新しい順）。coupons の fetchCoupons と同方針（tenantId 等値のみ + JS ソート）。
 */
export async function fetchSurveys(tenantId: string): Promise<PromoSurvey[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(collection(db, SURVEYS_COL), where('tenantId', '==', tid));
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<PromoSurvey, 'id'>),
  }));
  return items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/**
 * M-78 (promo): あるアンケートの回答（最新状態 doc）を取得する（回答分布の表示用）。
 *
 * Firestore Rules `promo_survey_responses/{id}` の read は admin + promo 契約自テナント。
 * 等値条件のみで composite index 不要。決定論的 doc id（1 ユーザー 1 doc）のため
 * 件数 = ユニーク回答者数（status='delivered' の配信ログ doc は M-76 で導入予定 / 現状なし）。
 */
export async function fetchSurveyResponsesBySurvey(
  tenantId: string,
  surveyId: string,
): Promise<PromoSurveyResponse[]> {
  const tid = tenantId || DEFAULT_TENANT_ID;
  const q = query(
    collection(db, SURVEY_RESPONSES_COL),
    where('tenantId', '==', tid),
    where('surveyId', '==', surveyId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<PromoSurveyResponse, 'id'>),
  }));
}
